import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import SupplierCompanyInfoAiService from 'src/modules/3rd-party-management/apis/common/SupplierCompanyInfoAiService';
import {
    buildAiRequestPayload,
    hasSeedValues,
} from '../constants/fieldDefinitions';

const useAiCompanyInfo = ({
    t,
    values,
    onSuccess,
}) => {
    const { mutate, isLoading } = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierCompanyInfoAiService.getInstance();
            return await service.fetchCompanyInfo(payload);
        },
        onSuccess: (response) => {
            if (!response?.ok || !response?.data) {
                toast(t('Unable to fetch company information from AI. Please fill in the remaining fields manually.'), {
                    type: 'warning',
                });
                onSuccess(null);
                return;
            }

            onSuccess(response.data);
        },
        onError: () => {
            toast(t('Unable to fetch company information from AI. Please fill in the remaining fields manually.'), {
                type: 'error',
            });
            onSuccess(null);
        },
    });

    const generateWithAi = useCallback(() => {
        if (!hasSeedValues(values)) {
            toast(t('Please complete all required fields before generating the form with AI.'), {
                type: 'warning',
            });
            return;
        }

        mutate(buildAiRequestPayload(values));
    }, [mutate, t, values]);

    const refreshAiData = useCallback(() => {
        if (!hasSeedValues(values)) {
            return;
        }

        mutate(buildAiRequestPayload(values));
    }, [mutate, values]);

    return {
        isLoading,
        canGenerate: hasSeedValues(values),
        generateWithAi,
        refreshAiData,
    };
};

export default useAiCompanyInfo;
